import { computed, ref } from 'vue';

import { hasWidgetVisitor, isWidgetSessionErrorKey, WIDGET_ERROR_KEY } from '@support/widget-contracts';
import type { WidgetSettings } from '@support/widget-contracts';

import { request, WidgetApiError } from '@/api/client';
import { START_WIDGET_SESSION } from '@/api/documents';
import { postToHost } from '@/bridge/host-bridge';
import type { WidgetContact, WidgetSession } from '@/api/types';

/**
 * The session token lives in this module-scope variable and NOWHERE else — not
 * `localStorage`, not `sessionStorage`, not a cookie. Two independent reasons,
 * either one sufficient:
 *
 * 1. Browsers partition third-party storage by the *top-level* site, so the
 *    same frame gets a different storage bucket on `admin.hope.cloud` than on
 *    `app.jetstream.studio`. Anything the widget "remembers" locally is
 *    remembered once per host app, which is not what anyone means by
 *    remembered. (Cookies are worse still: Safari and Firefox drop the frame's
 *    outright on the host where it is third-party.)
 *
 * 2. It would buy nothing even if it worked. Identity has to come from the host
 *    page on every load anyway — the host SPA mints a fresh assertion from the
 *    token it already holds — so a cached session token is at best a duplicate
 *    of something we can always re-derive, and at worst a stale credential
 *    sitting in storage that any script on the host page could read.
 */
let sessionToken: string | null = null;

/** What the exchange needs. Set by `configureSession` from `init` / `set`. */
let credentials: { widgetKey: string; userToken: string } | null = null;

/** Dedupes concurrent exchanges — two screens mounting at once must not mint two sessions. */
let exchange: Promise<string> | null = null;

/**
 * Which set of credentials the frame is working from. Bumped by EVERY change,
 * including a routine renewal of the same person's assertion.
 *
 * Internal, and deliberately not the thing the UI keys on. Its only job is to
 * let an in-flight request answer "are the credentials I started under still
 * current" — a counter rather than a boolean, because a visitor can change
 * twice while one exchange is outstanding.
 */
let credentialsEpoch = 0;

/**
 * Which PERSON the frame is serving. Bumped only when that actually changes.
 *
 * The distinction between this and {@link credentialsEpoch} is the whole point,
 * and collapsing the two was a real regression: an assertion lives minutes and
 * a renewal is by definition a different token string, so keying the panel on
 * "the token changed" tore down the panel every few minutes and took the
 * visitor's open conversation and half-typed reply with it —
 * `WidgetPanel.vue` explicitly promises the opposite.
 *
 * Two things advance it, and between them they cover both directions:
 *
 * - a credential change the frame did NOT ask for. The host swapped the
 *   visitor, and the previous person's threads must leave the screen at once,
 *   before the exchange that confirms it has even been sent.
 * - an exchange that comes back with a different `contact.id` than the one on
 *   screen. That catches the case the first cannot: a host that proactively
 *   pushes a token without waiting to be asked, which looks exactly like a
 *   renewal until the API says whose it is.
 */
const identityGeneration = ref(0);

/** Read-only view for components that key on the current identity. */
export const currentIdentity = computed(() => identityGeneration.value);

/**
 * Thrown by an exchange whose identity was replaced while it was in flight.
 *
 * Not a failure and never shown: the visitor who would have read it is no
 * longer the visitor the frame is serving. It exists so the stale call unwinds
 * without committing anything, and so the callers below can tell "abandoned"
 * apart from "went wrong" — which matters because the two want opposite things
 * from the UI. An abandoned exchange must leave the NEW identity's state alone;
 * a failed one must be reported.
 */
class WidgetIdentitySuperseded extends Error {
  constructor() {
    super('The widget identity changed while a session exchange was in flight.');
    this.name = 'WidgetIdentitySuperseded';
  }
}

/**
 * The `userToken` we have already asked the host to replace.
 *
 * The budget is one request per token the host hands us. If the replacement
 * fails the same way, that is a real failure — asking again would be a loop
 * whose every iteration produces the identical answer.
 */
let identityRequestedFor: string | null = null;

/**
 * How long the frame waits for the host to answer `identityExpired` before
 * giving up. NOT a refresh timer: the frame cannot mint an assertion and must
 * never guess at when one is due — this only bounds the wait, so a publisher
 * who never handles the event gets an honest error instead of a spinner that
 * never resolves (which would be the same dead-end with a friendlier word).
 */
const IDENTITY_RENEWAL_TIMEOUT_MS = 15_000;

/**
 * What a visitor is told when identity could not be re-established — whichever
 * way it failed: the host stayed silent, or it answered with a token that did
 * not work either.
 *
 * This exists because the API's own assertion messages ("The Hope.Cloud
 * identity assertion has expired") are written for the developer wiring up the
 * minting call, and they are right to be: whoever is debugging that reads
 * `extensions.key` and their console. But the frame is the only party that
 * knows a string is about to be rendered into a panel a customer is looking at,
 * so translating for that audience is the frame's job, not the API's. The key
 * rides along unchanged, so nothing is lost for the engineer.
 */
const RECONNECT_FAILED_MESSAGE = 'Support could not reconnect. Reloading the page will restore it.';

export type SessionStatus = 'idle' | 'loading' | 'ready' | 'error';

export const contact = ref<WidgetContact | null>(null);

export const sessionStatus = ref<SessionStatus>('idle');

export const sessionErrorMessage = ref<string | null>(null);

/** True while the frame is waiting for the host to hand it a fresh assertion. */
export const isRenewingIdentity = ref(false);

/**
 * The failures a fresh assertion could plausibly fix. `EXPIRED` is the ordinary
 * one; `INVALID` is included because a key rotation on the issuer's side
 * presents that way and is equally answered by minting a new token. Neither can
 * loop — {@link identityRequestedFor} spends the budget either way.
 *
 * Belongs beside `isWidgetSessionErrorKey` in `@support/widget-contracts` if the
 * API ever needs the same grouping; kept local until something else wants it.
 */
const isRenewableAssertionErrorKey = (key: string | undefined): boolean =>
  key === WIDGET_ERROR_KEY.ASSERTION_EXPIRED || key === WIDGET_ERROR_KEY.ASSERTION_INVALID;

/** Resolved by `configureSession` when the host delivers a different token. */
let pendingIdentity: {
  promise: Promise<void>;
  resolve: () => void;
  reject: () => void;
  timer: number;
} | null = null;

const waitForFreshIdentity = (): Promise<void> => {
  if (pendingIdentity) {
    return pendingIdentity.promise;
  }

  let resolve!: () => void;
  let reject!: () => void;

  const promise = new Promise<void>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  const timer = window.setTimeout(() => {
    pendingIdentity = null;
    reject();
  }, IDENTITY_RENEWAL_TIMEOUT_MS);

  pendingIdentity = { promise, resolve, reject, timer };

  return promise;
};

/**
 * Give up on a parked renewal that cannot be answered.
 *
 * Reached when the host clears `userToken` — a logout — while a call is waiting
 * on `identityExpired`. Without this the wait sat there until its timeout while
 * the panel showed the previous visitor, and nothing could clear it: the only
 * thing that lifts a renewal is an exchange, and an exchange needs credentials
 * that have just been taken away.
 */
const abandonFreshIdentity = (): void => {
  if (!pendingIdentity) {
    return;
  }

  window.clearTimeout(pendingIdentity.timer);

  const { reject } = pendingIdentity;

  pendingIdentity = null;

  reject();
};

const settleFreshIdentity = (): void => {
  if (!pendingIdentity) {
    return;
  }

  window.clearTimeout(pendingIdentity.timer);

  const { resolve } = pendingIdentity;

  pendingIdentity = null;

  resolve();
};

/**
 * Apply the settings the host stated, and decide what that means for the person
 * on screen.
 *
 * `source` is the message the settings arrived on, and it is what replaced
 * timing as the discriminator. Guessing from "was a renewal parked when this
 * token turned up" was wrong three review rounds running: it preserved the
 * outgoing visitor's panel whenever a host answered a parked `identityExpired`
 * with somebody else's token, and it destroyed the incoming composer whenever a
 * host rotated the same person's assertion without being asked.
 *
 * The host already says which it means, through the API it calls:
 *
 * - **`init`** states the complete configuration. It is how a host declares a
 *   visitor, so it is treated as possibly a DIFFERENT one: identity advances,
 *   the panel and everything in it — threads, contact, composer — is discarded.
 * - **`set`** updates a running widget, and is the documented answer to
 *   `identityExpired`. It is treated as the same visitor: the panel survives,
 *   which is what keeps a half-typed reply alive across a renewal that happens
 *   every few minutes.
 *
 * A host that switches visitors through `set` is misusing the contract, and the
 * exposure that buys is bounded rather than removed: the frame holds no session
 * until the exchange returns, so the incoming visitor can briefly SEE the
 * previous panel but cannot read or write anything as them, and the contact the
 * API names then advances the identity and discards it. That residual is the
 * price of not blanking a composer every few minutes, which is the alternative.
 */
export const configureSession = (
  settings: WidgetSettings,
  // Defaults to the DESTRUCTIVE reading. A forgotten argument should discard a
  // panel that did not need discarding, never preserve one belonging to
  // somebody who has left.
  source: 'init' | 'set' = 'init',
): void => {
  // No credentials for nobody. A token that names no organization is held to
  // the same rule as a missing one, so it is never sent to the exchange: the
  // API would refuse it as invalid, and an invalid assertion is something the
  // frame asks the host to replace — a question with no good answer, raised for
  // a person the host has no assertion for.
  const next = hasWidgetVisitor(settings)
    ? { widgetKey: settings.widgetKey, userToken: settings.userToken }
    : null;

  const changed = next?.widgetKey !== credentials?.widgetKey
    || next?.userToken !== credentials?.userToken;

  credentials = next;

  // A `set` that swaps the visitor (host app signed someone else in) must not
  // keep serving the previous person's session.
  if (changed) {
    // First, and before anything else clears: an exchange started under the
    // outgoing credentials is still in flight, and this is what tells it not to
    // commit. Clearing the fields below is not enough on its own — the stale
    // request writes them back when it resolves.
    credentialsEpoch += 1;

    // A renewal, and nothing else, preserves what is on screen. Clearing the
    // token is a logout and is never a renewal, whatever it arrived on.
    const isRenewal = source === 'set' && next !== null;

    sessionToken = null;
    exchange = null;
    identityRequestedFor = null;
    sessionErrorMessage.value = null;

    if (!isRenewal) {
      identityGeneration.value += 1;
      contact.value = null;
      sessionStatus.value = 'idle';
    }

    // A fresh token is also the answer to `identityExpired`. Waking the parked
    // call here is what makes recovery automatic: the visitor never clicks, the
    // call that failed simply continues.
    //
    // Clearing the token is not an answer — it is a logout — and it used to be
    // left parked "until it times out". That was wrong in a way the timeout did
    // not save: the wait rejected, but nothing else moved, so the panel kept
    // showing the person who had just logged out and no exchange could ever run
    // to correct it. Now it is abandoned explicitly, and the branch above has
    // already torn the panel down.
    if (next) {
      settleFreshIdentity();
    } else {
      abandonFreshIdentity();
    }
  }
};

const runExchange = async (): Promise<string> => {
  if (!credentials) {
    // Nobody to exchange for. Nothing on screen can reach this — the frame
    // draws no launcher and no panel without a visitor — so it is here for a
    // call already under way when the host withdrew them, which must fail
    // closed rather than ask the API to sign in nobody.
    throw new WidgetApiError('Support is not available without a signed-in visitor.');
  }

  const startedFor = credentialsEpoch;

  let data: { startWidgetSession: WidgetSession };

  try {
    data = await request<{ startWidgetSession: WidgetSession }>(
      START_WIDGET_SESSION,
      { input: { widgetKey: credentials.widgetKey, userToken: credentials.userToken } },
    );
  } catch (error) {
    // A FAILURE also has to be disowned once the credentials have moved on, and
    // that was missed at first: the epoch was checked only on the success path,
    // so a stale exchange's rejection was handled as though it belonged to the
    // visitor who is signed in NOW. Concretely, the outgoing person's expired
    // assertion asked the host to mint a fresh one for the incoming person, and
    // their unrelated network error put the panel into `error` for somebody
    // whose credentials are perfectly good.
    if (startedFor !== credentialsEpoch) {
      throw new WidgetIdentitySuperseded();
    }

    throw error;
  }

  // The credentials moved on while this was in flight. Committing now would
  // install the PREVIOUS visitor's session and contact over the current one —
  // a sign-in that silently reverts, and the reverted-to person is somebody
  // else. There is no request to abort here (the fetch has already answered);
  // refusing to write is the whole of the fix.
  if (startedFor !== credentialsEpoch) {
    throw new WidgetIdentitySuperseded();
  }

  const token = data.startWidgetSession.sessionToken;
  const next = data.startWidgetSession.contact;

  // The API has now named whose token that was, which is the only authoritative
  // answer. A different person than the one on screen means the panel is still
  // showing somebody else's threads — so discard them here even though
  // `configureSession` decided this looked like a renewal. This is the branch
  // that covers a host pushing a fresh token without being asked.
  if (contact.value && contact.value.id !== next.id) {
    identityGeneration.value += 1;
  }

  sessionToken = token;
  contact.value = next;

  return token;
};

const ensureSession = async (): Promise<string> => {
  if (sessionToken) {
    return sessionToken;
  }

  if (!exchange) {
    // Cleared only if it is still OURS. An unconditional `exchange = null` in
    // the `finally` meant a stale exchange settling late wiped the reference to
    // the CURRENT visitor's in-flight one — so the next caller started a second
    // exchange beside it, and the two raced to install a session.
    //
    // The comparison is against `tracked`, the promise actually stored, not
    // against the one `runExchange()` returned: `.finally()` produces a NEW
    // promise, so comparing with the original never matches and the slot is
    // never released at all — which is the same class of mistake, in the other
    // direction, and is why this is written out rather than chained.
    const tracked: Promise<string> = runExchange().finally(() => {
      if (exchange === tracked) {
        exchange = null;
      }
    });

    exchange = tracked;
  }

  return exchange;
};

/**
 * Asks the host for a new assertion, waits for it, and exchanges it.
 *
 * The frame cannot mint an assertion — only the host SPA can, from the
 * backoffice credential it already holds — so this is the ONLY recovery from an
 * expired `userToken`, and without it the panel dead-ends until the host
 * happens to run `init` again. Rethrows the original failure when the situation
 * is not one a fresh assertion fixes.
 */
const requestFreshIdentity = async (cause: unknown): Promise<string> => {
  const key = cause instanceof WidgetApiError ? cause.key : undefined;

  if (!isRenewableAssertionErrorKey(key) || !credentials) {
    throw cause;
  }

  // Join a renewal already in flight instead of failing beside it. Two screens
  // can hit the same expired assertion in one tick; only one of them needs to
  // ask, and the other must not surface a raw API error while the first is
  // quietly recovering.
  if (!pendingIdentity) {
    if (identityRequestedFor === credentials.userToken) {
      // Already asked for this exact token and it still fails. Indistinguishable
      // from a silent host as far as the person is concerned, so say the same
      // thing rather than leaking the API's developer-facing wording.
      throw new WidgetApiError(RECONNECT_FAILED_MESSAGE, key);
    }

    identityRequestedFor = credentials.userToken;

    postToHost({ type: 'identityExpired' });
  }

  isRenewingIdentity.value = true;

  try {
    try {
      await waitForFreshIdentity();
    } catch {
      throw new WidgetApiError(RECONNECT_FAILED_MESSAGE, key);
    }

    try {
      return await ensureSession();
    } catch (renewalError) {
      // The replacement assertion failed too. Only assertion failures are
      // translated — anything else (a rate limit, an unknown publisher, a
      // network fault) already carries wording meant for a person, and
      // replacing it would lose real information.
      if (renewalError instanceof WidgetApiError && isRenewableAssertionErrorKey(renewalError.key)) {
        throw new WidgetApiError(RECONNECT_FAILED_MESSAGE, renewalError.key);
      }

      throw renewalError;
    }
  } finally {
    isRenewingIdentity.value = false;
  }
};

/**
 * A session, renewing the identity behind it if that is what is missing.
 *
 * Both entry points go through here, and both need to. The arithmetic that
 * makes this necessary — assertion ~5 minutes, session ~1 hour — breaks a panel
 * left OPEN past the hour, and equally a panel first OPENED an hour after the
 * page loaded, where the very first exchange is the one holding a stale token.
 */
const ensureSessionOrRenew = async (): Promise<string> => {
  try {
    return await ensureSession();
  } catch (error) {
    return await requestFreshIdentity(error);
  }
};

/**
 * Runs one authenticated call, and answers an expired/invalid/missing session
 * by re-exchanging and replaying the call ONCE. A panel left open outlives its
 * session, so this is the normal path, not an error path — but a second failure
 * is a real failure and is thrown.
 */
export const withSession = async <T>(run: (token: string) => Promise<T>): Promise<T> => {
  // Captured before anything can await. `run` is a closure over what the
  // visitor typed — a reply, a new thread's title and body — and replaying it
  // under a session belonging to somebody else would post one person's words as
  // another's. That is reachable without any bug in the caller: A's call parks
  // waiting on `identityExpired`, the host signs B in, the parked call resumes.
  const startedAs = identityGeneration.value;

  // One check, not two. An earlier version also compared `contact.id` here, on
  // the theory that a parked call answered with somebody else's token would
  // slip past a generation that `configureSession` deliberately left alone.
  // A mutation test showed that check never fires: `runExchange` advances the
  // generation itself the moment the API names a different contact, so by the
  // time this runs the counter has already moved. A second guard that cannot
  // fail is worse than none — it reads like the control, and hides which line
  // is actually holding the property.
  const forCurrentVisitor = (): void => {
    if (startedAs !== identityGeneration.value) {
      throw new WidgetIdentitySuperseded();
    }
  };

  const token = await ensureSessionOrRenew();

  forCurrentVisitor();

  try {
    return await run(token);
  } catch (error) {
    if (!(error instanceof WidgetApiError) || !isWidgetSessionErrorKey(error.key)) {
      throw error;
    }

    // BEFORE clearing anything. `sessionToken` is module-global, so a stale
    // request answering SESSION_TOKEN_EXPIRED after the visitor changed would
    // otherwise discard the session the CURRENT visitor just established, and
    // then start an exchange on their behalf that can fail — costing them a
    // working session over somebody else's obsolete request. The clear has to
    // sit behind the same guard as the replay, not in front of it.
    forCurrentVisitor();

    sessionToken = null;

    const renewed = await ensureSessionOrRenew();

    // Checked again, not just once at the top: the whole point of this retry is
    // that it awaits a renewal, and the visitor can change during exactly that
    // await. A renewal for the same person passes both checks untouched.
    forCurrentVisitor();

    return run(renewed);
  }
};

/** Kicks off the exchange for the UI, tracking it in the exported refs. */
export const startSession = async (): Promise<void> => {
  if (sessionStatus.value === 'loading' || sessionStatus.value === 'ready') {
    return;
  }

  sessionStatus.value = 'loading';
  sessionErrorMessage.value = null;

  try {
    await ensureSessionOrRenew();
    sessionStatus.value = 'ready';
  } catch (error) {
    // Abandoned, not failed. `configureSession` has already reset the status
    // for the identity that replaced this one, and the visitor this error was
    // written for is not the one now looking at the panel — reporting it here
    // would put a stale error over a fresh sign-in.
    if (error instanceof WidgetIdentitySuperseded) {
      return;
    }

    sessionStatus.value = 'error';
    sessionErrorMessage.value = error instanceof Error
      ? error.message
      : 'Could not start a support session.';
  }
};

/** Lets the UI retry after a failed exchange. */
export const retrySession = async (): Promise<void> => {
  sessionStatus.value = 'idle';

  return startSession();
};
